// A share link (/r/<token>), a client pack (/p/<token>) and the print pages the PDF function opens
// (/p/print/<token>, /p/print/<token>/c/<candidate>, /p/<token>/pdf) have a secret in the address.
// Anything that records the address (analytics, error tracking) must not see it. The same path rule
// is written out in public/index.html for Bing and the Google sign-in script, which load before this
// file can; tests/offline/keyed-address.test.cjs keeps the two in step.
export const KEYED_ADDRESS_PATH = /^\/(?:r|p)(?:\/|$)/

export function isKeyedAddress(pathname) {
  return KEYED_ADDRESS_PATH.test(String(pathname || ''))
}

// Replaces the token in every /r/<token>, /p/<token> and /p/print/<token> it finds (and in the API paths below), in a bare path or
// a full address: "/r/abc?x=1" becomes "/r/[token]?x=1". Text with no such address comes back unchanged.
const ADDRESS = /((?:https?:)?\/\/[^/\s"'?#]+|^)\/(r|p)\/(print\/)?([^/?#\s"'<>)]+)/g
// The API calls these pages make carry the same token (or the print key) in their path, and Sentry records
// every fetch and XHR as a breadcrumb: "assessment/public/report-shares/<token>" becomes "...[token]".
const API = /(public\/(?:report-shares|client-packs|client-pack-prints)\/)([^/?#\s"'<>)]+)/g

export function scrubKeyedAddress(text) {
  if (typeof text !== 'string' || text.indexOf('/') === -1) return text
  return text
    .replace(ADDRESS, (all, origin, kind, print) => origin + '/' + kind + '/' + (print || '') + '[token]')
    .replace(API, (all, prefix) => prefix + '[token]')
}

// Every string in a Sentry event or breadcrumb (request URL, Referer, transaction, breadcrumb data and
// messages, exception text), scrubbed in place. The depth limit keeps a self-referencing object finite.
export function scrubKeyedAddresses(value, depth = 0) {
  if (typeof value === 'string') return scrubKeyedAddress(value)
  if (!value || typeof value !== 'object' || depth > 8) return value
  for (const key of Object.keys(value)) {
    const next = scrubKeyedAddresses(value[key], depth + 1)
    if (next !== value[key]) value[key] = next
  }
  return value
}

// The two Sentry hooks. Only events and breadcrumbs that carry a keyed address are touched.
export const sentryBeforeSend = event => scrubKeyedAddresses(event)
export const sentryBeforeBreadcrumb = breadcrumb => scrubKeyedAddresses(breadcrumb)
